import type React from 'react';
import {Canvas, Textbox} from '@postermywall/fabricjs-2';
import type * as Fabric from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {SelectedTimelineItem} from '@Components/poster-editor/components/timeline/timeline.types';
import {TimelineItemType} from '@Components/poster-editor/components/timeline/timeline.types';
import {canSplitTimelineItem} from '@Components/poster-editor/components/timeline/utils/timeline-calculations';
import {getCurrentPageTime} from '@Components/poster-editor/poster-editor-reducer';
import {copyToClipboard} from '@Utils/clipboard.util';

/**
 * True when the active selection is a Textbox that is currently being edited.
 * Used to switch the popup to text-edit mode (cut/copy/paste/select-all).
 */
export const isTextEditing = (currentPage: Page | undefined): boolean => {
  const activeObject = currentPage?.activeSelection.getActiveObject();
  return activeObject instanceof Textbox && activeObject.isEditing;
};

/**
 * True when more than one object is selected (a group selection).
 */
export const isGroupSelection = (canvas: Canvas | undefined, target: Fabric.FabricObject | undefined): boolean => {
  if (!canvas || !target) {
    return false;
  }
  const activeObjects = canvas.getActiveObjects();
  return !!activeObjects && activeObjects.length > 1 && canvas.getActiveObject() === target;
};

interface GuardCtx {
  target: Fabric.FabricObject | undefined;
  canvas: Canvas | undefined;
  currentPage: Page | undefined;
  activeItem: ItemType | null;
}

const hasValidTarget = (ctx: GuardCtx): boolean => {
  return !!ctx.target && ctx.canvas instanceof Canvas;
};

export const shouldShowPositionSubmenu = (ctx: GuardCtx): boolean => {
  if (!hasValidTarget(ctx)) {
    return false;
  }
  if (isGroupSelection(ctx.canvas, ctx.target)) {
    return true;
  }
  if (isTextEditing(ctx.currentPage)) {
    return false;
  }
  return !!ctx.activeItem?.canMoveInZIndex();
};

export const shouldShowItemReplaceSubmenu = (ctx: GuardCtx): boolean => {
  if (!hasValidTarget(ctx) || isTextEditing(ctx.currentPage)) {
    return false;
  }
  // Backgrounds get their own Replace submenu (shouldShowBackgroundReplaceSubmenu); excluding them here avoids a duplicate.
  if (!ctx.activeItem || ctx.activeItem.isBackground) {
    return false;
  }
  return ctx.activeItem.isImage() || ctx.activeItem.isVideo();
};

export const shouldShowAlignSubmenu = (ctx: GuardCtx): boolean => {
  if (!hasValidTarget(ctx) || isTextEditing(ctx.currentPage)) {
    return false;
  }
  // Show for single items and for group selections (align applies to both).
  return !!ctx.activeItem || isGroupSelection(ctx.canvas, ctx.target);
};

export const shouldShowSelectLayersItem = (ctx: GuardCtx): boolean => {
  if (!hasValidTarget(ctx) || isTextEditing(ctx.currentPage)) {
    return false;
  }
  return !!ctx.activeItem || isGroupSelection(ctx.canvas, ctx.target);
};

export const shouldShowAiSubtitlesItem = (ctx: GuardCtx): boolean => {
  if (!hasValidTarget(ctx) || isTextEditing(ctx.currentPage) || isGroupSelection(ctx.canvas, ctx.target)) {
    return false;
  }
  return !!ctx.activeItem?.isVideo();
};

export const shouldShowEditContentItem = (ctx: GuardCtx): boolean => {
  if (!hasValidTarget(ctx) || isTextEditing(ctx.currentPage) || isGroupSelection(ctx.canvas, ctx.target)) {
    return false;
  }
  return !!ctx.activeItem?.isMenu() || !!ctx.activeItem?.isTableItem();
};

export const shouldShowSplitItem = (selectedTimelineItem: SelectedTimelineItem | undefined): boolean => {
  if (!selectedTimelineItem) {
    return false;
  }
  const state = window.PMW.redux.store.getState();
  const currentPageTime = getCurrentPageTime(state);
  return canSplitTimelineItem(selectedTimelineItem, currentPageTime);
};

export const shouldShowAudioReplaceSubmenu = (selectedTimelineItem: SelectedTimelineItem | undefined): boolean => {
  return selectedTimelineItem?.type === TimelineItemType.AUDIO;
};

export const shouldShowBackgroundReplaceSubmenu = (selectedTimelineItem: SelectedTimelineItem | undefined): boolean => {
  if (selectedTimelineItem?.type !== TimelineItemType.BACKGROUND) {
    return false;
  }
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  const bg = page?.items.itemsHashMap[selectedTimelineItem.id];
  return !!bg && (bg.isImageBackground() || bg.isVideo());
};

/* -------------------------------------------------------------------------- */
/* Text-edit clipboard helpers                                                */
/* -------------------------------------------------------------------------- */

export const cutFromText = async (canvas: Canvas | undefined, activeItem: ItemType | null): Promise<void> => {
  const activeObject = canvas?.getActiveObject();
  if ((activeItem?.isText() || activeItem?.isSlideshow()) && canvas && activeObject instanceof Textbox) {
    const selectionStart = activeObject.hiddenTextarea ? activeObject.hiddenTextarea.selectionStart : activeObject.selectionStart;
    const selectionEnd = activeObject.hiddenTextarea ? activeObject.hiddenTextarea.selectionEnd : activeObject.selectionEnd;
    await copyToClipboard(activeObject.text.slice(selectionStart, selectionEnd));
    const prevText = activeObject.text;
    const updatedText = prevText.substring(0, selectionStart) + prevText.substring(selectionEnd, prevText.length);
    activeItem.updateActiveText(updatedText);
    activeObject.selectionStart = selectionStart;
    activeObject.selectionEnd = selectionStart;
  }
};

export const copyFromText = async (canvas: Canvas | undefined): Promise<void> => {
  const activeObject = canvas?.getActiveObject();
  if (canvas && activeObject instanceof Textbox) {
    const selectionStart = activeObject.hiddenTextarea ? activeObject.hiddenTextarea.selectionStart : activeObject.selectionStart;
    const selectionEnd = activeObject.hiddenTextarea ? activeObject.hiddenTextarea.selectionEnd : activeObject.selectionEnd;
    await copyToClipboard(activeObject.text.slice(selectionStart, selectionEnd));
    activeObject.selectionStart = selectionEnd;
    activeObject.selectionEnd = selectionEnd;
  }
};

export const pasteIntoText = async (canvas: Canvas | undefined, activeItem: ItemType | null, e?: Event): Promise<void> => {
  const activeObject = canvas?.getActiveObject();
  if ((activeItem?.isText() || activeItem?.isSlideshow()) && canvas && activeObject instanceof Textbox) {
    await activeItem.onPasteText(e);
  }
};

export const selectAllText = (canvas: Canvas | undefined): void => {
  const activeObject = canvas?.getActiveObject();
  if (activeObject instanceof Textbox) {
    activeObject.selectionStart = 0;
    activeObject.selectionEnd = activeObject.text.length;
    canvas?.requestRenderAll();
  }
};

export const copySelection = (currentPage: Page | undefined, e?: React.MouseEvent<HTMLElement>): void => {
  currentPage?.clipboard.copySelection(e);
};

interface PasteOffset {
  left?: number;
  top?: number;
}

export const pasteGraphicItems = async (
  currentPage: Page | undefined,
  eventCanvasOffset: PasteOffset | undefined,
  e?: React.MouseEvent<HTMLElement>
): Promise<void> => {
  if (currentPage) {
    await currentPage.clipboard.pasteItems(e, {
      isPasteFromRightClick: true,
      eventCanvasOffset: {left: eventCanvasOffset?.left, top: eventCanvasOffset?.top},
    });
  }
};

export const toggleMultiSelect = (currentlyEnabled: boolean | undefined): void => {
  window.posterEditor?.whiteboard?.itemsMultiSelect.updateFromObject({
    enabled: !currentlyEnabled,
  });
};
